/**
 * EL ADAPTADOR DE LA VITRINA DE AUTOS Y DEL SELLO.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  ESTE ES EL ÚNICO ARCHIVO QUE CONOCE LA FORMA DEL API PARA `listings`.    ║
 * ║  Las pantallas (`SellWizard`, `MyListings`, `InspectionForm`,             ║
 * ║  `Suggestions`) siguen hablando en los nombres que la app ya usa —        ║
 * ║  `price`, `photos`, `province`, `status:'sold'`— y la traducción vive     ║
 * ║  aquí y en ningún otro sitio.                                            ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * ─── LAS TRAMPAS DE ESTA TRADUCCIÓN, POR ORDEN DE LO QUE CUESTAN ────────────
 *
 *  1. 🔴 LOS ESQUEMAS DEL API SON `.strict()`. Un campo de más NO se ignora: es
 *     un 400 que NOMBRA el campo. El objeto que `SellWizard` arma para Firestore
 *     lleva `sellerId`, `sellerName`, `sellerPhoto`, `workshopId`,
 *     `sellerCategory`, `healthScore`, `inspected`, `status`, `views`,
 *     `currency`, `acceptsTrade`, `bodyType`, `lat`, `lng` y las marcas de
 *     tiempo — y NINGUNO puede viajar: el servidor los DERIVA (probado contra el
 *     API: `sellerId` devuelve «campo no permitido: sellerId»). Por eso el
 *     cuerpo se construye campo a campo, con lista de PERMITIDOS, y jamás con un
 *     `...data`: un `...` es una lista de prohibidos disfrazada y falla al
 *     AÑADIR un campo.
 *  2. 🔴 LOS PRECIOS VAN EN CENTAVOS ENTEROS (`z.number().int()`). En Firestore
 *     `price` son pesos.
 *  3. 🔴 VENTA Y RENTA SON EXCLUYENTES. El API rechaza un anuncio de venta con
 *     tarifas de renta y viceversa; Firestore no lo miraba. Aquí solo se manda
 *     el bloque que corresponde al `listingType`.
 *  4. 🔴 LOS ESTADOS VAN EN MAYÚSCULAS (`SOLD`, `PAUSED`, `REMOVED`) y son un
 *     enum de la base. La app los escribe en minúscula.
 *  5. 🔴 `acceptsTrade` NO EXISTE en el API. Se pierde en el camino de Postgres
 *     y está anotado abajo: mandarlo sería un 400.
 */
import { apiFetch, ApiError } from './client';

/** El mismo tope que `MAX_PAGE_SIZE` en api/src/services/listings.ts. */
export const LISTINGS_PAGE_SIZE = 50;

/** El mismo tope de profundidad que `MAX_PAGE`. Pedir la 201 es un 400. */
export const LISTINGS_MAX_PAGE = 200;

/** Los mismos límites de fotos que el servidor (`MIN/MAX_LISTING_PHOTOS`). */
export const MIN_LISTING_PHOTOS = 4;
export const MAX_LISTING_PHOTOS = 6;

/** Lo que se rechaza ANTES de salir a la red. Lleva `code` para poder traducirlo. */
export class ListingInputError extends Error {
  constructor(message, code = 'invalid_input') {
    super(message);
    this.name = 'ListingInputError';
    this.code = code;
  }
}

/** Texto con contenido, o `null`. `''` no es un valor, es un campo vacío. */
function nulable(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  return s === '' ? null : s;
}

/** Pesos → centavos enteros. `null` si no hay número que convertir. */
function centavos(v) {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/** Centavos → pesos, para devolverle a la pantalla lo que ya sabe leer. */
function pesos(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n / 100 : null;
}

function entero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

/** 'sold' → 'SOLD'. El enum de la base va en mayúsculas. */
const STATUS_API = {
  draft: 'DRAFT',
  active: 'ACTIVE',
  reserved: 'RESERVED',
  sold: 'SOLD',
  paused: 'PAUSED',
  removed: 'REMOVED',
};

/** 'CAR' → 'car'. La app las lee en minúscula. */
function minusculas(v) {
  return typeof v === 'string' ? v.toLowerCase() : v;
}

/** 'car' → 'CAR'. `vehicleType` de la app tiene 'moto' y 'carro'. */
const VEHICLE_CATEGORY_API = { car: 'CAR', carro: 'CAR', moto: 'MOTO', both: 'BOTH' };

/** 'ok' → 'OK'. Los 12 puntos de la inspección son un enum en la base. */
const CHECKPOINT_STATUS_API = { ok: 'OK', attention: 'ATTENTION', fail: 'FAIL', na: 'NA' };

/**
 * EL CUERPO DEL ANUNCIO, a partir del objeto que el wizard ya arma.
 *
 * `create` añade `listingType` y `vehicleId` (el carro registrado del que sale
 * el historial; tiene que ser tuyo y el servidor lo comprueba). En la edición
 * ninguno de los dos viaja: cambiar de qué carro cuelga un anuncio publicado es
 * cambiarle el historial al comprador.
 *
 * ⚠️ LO QUE SE PIERDE al pasar por aquí, y hay que saberlo:
 *   · `acceptsTrade` ("recibe en parte de pago") — no existe en el API.
 *   · `bodyType`, `lat`, `lng`, `currency` — tampoco.
 *   · la salud (`healthScore`/`healthVerified`/`serviceCount`/`lastServiceDate`)
 *     — la DERIVA el servidor del historial del carro registrado, que es
 *     justamente lo que arregla: hoy la declara el vendedor.
 */
export function listingApiBody(data = {}, { create = false } = {}) {
  const esRenta = String(data.listingType || 'sale').toLowerCase() === 'rent';

  const fotos = Array.isArray(data.photos) ? data.photos.filter(Boolean) : [];
  if (create && fotos.length < MIN_LISTING_PHOTOS) {
    // No se sale a la red a que nos digan que no: el viaje ya está pagado en
    // subidas a Cloudinary que el vendedor haría para nada.
    throw new ListingInputError(
      `photos: hacen falta al menos ${MIN_LISTING_PHOTOS} fotos del carro.`,
      'too_few_photos',
    );
  }
  if (fotos.length > MAX_LISTING_PHOTOS) {
    throw new ListingInputError(
      `photos: como máximo ${MAX_LISTING_PHOTOS} fotos del carro.`,
      'too_many_photos',
    );
  }

  const anio = Number(data.year);
  const body = {
    brand: typeof data.brand === 'string' ? data.brand.trim() : '',
    model: typeof data.model === 'string' ? data.model.trim() : '',
    // 🔴 En Firestore `year` era number, string ('2018') o '' en la misma
    // colección. Aquí el tipo manda, y el carro sin año cierto va en `isVintage`
    // (antes se colaba como el literal 'vintage').
    year: Number.isFinite(anio) && anio > 0 ? Math.trunc(anio) : null,
    isVintage: data.isVintage === true || String(data.year || '').toLowerCase() === 'vintage',
    trim: nulable(data.trim),
    color: nulable(data.color),
    transmission: nulable(data.transmission),
    fuelType: nulable(data.fuelType),
    mileageKm: entero(data.mileageKm),
    vehicleCategory:
      VEHICLE_CATEGORY_API[String(data.vehicleCategory || data.bodyType || 'car').toLowerCase()] || 'CAR',
    negotiable: data.negotiable === true,
    marbeteValid: data.marbeteValid === true,
    matriculaReady: data.matriculaReady === true,
    traspasoReady: data.traspasoReady === true,
    noLien: data.noLien === true,
    reason: nulable(data.reason),
    singleOwner: data.singleOwner === true,
    videoUrl: nulable(data.videoURL ?? data.videoUrl),
    provinceCode: nulable(data.province ?? data.provinceCode),
    city: nulable(data.city),
    photos: fotos,
  };

  // 🔴 LO UNO O LO OTRO. Mandar los dos bloques es un 400 del servidor, y hasta
  // hoy nada impedía en Firestore un anuncio de renta con precio de venta.
  if (esRenta) {
    body.rentPriceDayCents = centavos(data.rentPricePerDay ?? data.rentPriceDayCents);
    body.rentPriceWeekCents = centavos(data.rentPricePerWeek ?? data.rentPriceWeekCents);
    body.rentPriceMonthCents = centavos(data.rentPricePerMonth ?? data.rentPriceMonthCents);
  } else {
    body.priceCents = centavos(data.price ?? data.priceCents);
  }

  if (create) {
    body.listingType = esRenta ? 'RENT' : 'SALE';
    body.vehicleId = data.vehicleId || null;
  }
  return body;
}

/** El acuse de una escritura, en los nombres que la app ya usa. */
export function adaptListingAck(payload) {
  if (!payload || typeof payload !== 'object') {
    throw new ApiError('El API no devolvió el anuncio guardado.', { code: 'bad_response' });
  }
  return {
    id: payload.id,
    listingType: minusculas(payload.listingType) || 'sale',
    status: minusculas(payload.status) || 'active',
    healthScore: payload.healthScore ?? null,
    healthVerified: payload.healthVerified === true,
    serviceCount: Number(payload.serviceCount) || 0,
    photoCount: Number(payload.photoCount) || 0,
    updatedAt: payload.updatedAt ?? null,
  };
}

/** Una tarjeta de la vitrina, en los nombres que `ListingCard` ya lee. */
export function adaptListing(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  return {
    ...row,
    id: row.id,
    listingType: minusculas(row.listingType) || 'sale',
    status: minusculas(row.status) || 'active',
    price: pesos(row.priceCents),
    rentPricePerDay: pesos(row.rentPriceDayCents),
    rentPricePerWeek: pesos(row.rentPriceWeekCents),
    rentPricePerMonth: pesos(row.rentPriceMonthCents),
    province: row.provinceCode ?? null,
    videoURL: row.videoUrl ?? null,
    photos: Array.isArray(row.photos) ? row.photos : [],
  };
}

/** Una página de la vitrina. El TOTAL es lo que permite decir "página 1 de 7". */
export function adaptListingPage(payload, { page = 1, pageSize = LISTINGS_PAGE_SIZE } = {}) {
  const lista = payload && payload.items;
  if (!Array.isArray(lista)) {
    throw new ApiError('El API no devolvió una lista de anuncios.', { code: 'bad_response' });
  }
  const tam = Number(payload.pageSize) || pageSize;
  const total = Number(payload.total);
  return {
    listings: lista.map(adaptListing).filter(Boolean),
    page: Number(payload.page) || page,
    pageSize: tam,
    total: Number.isFinite(total) ? total : null,
    totalPages: Number(payload.totalPages) || null,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// LA VITRINA
// ────────────────────────────────────────────────────────────────────────────

/** Una página de anuncios. Los filtros BAJAN AL SQL, no se aplican aquí. */
export async function fetchListings(params = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const page = Math.min(Math.max(Number(params.page) || 1, 1), LISTINGS_MAX_PAGE);
  const pageSize = Number(params.pageSize) || LISTINGS_PAGE_SIZE;
  const query = { page, pageSize };
  if (params.listingType) query.listingType = String(params.listingType).toUpperCase();
  if (params.sellerId) query.sellerId = params.sellerId;
  if (params.sort) query.sort = params.sort;
  const payload = await call('/listings', { query, signal });
  return adaptListingPage(payload, { page, pageSize });
}

/**
 * La FICHA de un anuncio.
 *
 * ⚠️ EL 404 NO ES UN ERROR, ES UNA RESPUESTA: el API responde 404 cuando el
 * anuncio no existe Y cuando está restringido y no eres su vendedor ni el
 * admin, a propósito y para que no se puedan distinguir. Cualquier OTRO fallo
 * SÍ sube: una red caída no puede parecer un anuncio borrado.
 */
export async function fetchListing(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    return adaptListing(await call(`/listings/${encodeURIComponent(id)}`, { signal }));
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// PUBLICAR Y GESTIONAR
// ────────────────────────────────────────────────────────────────────────────

/** Publica el anuncio. El acuse trae la salud DERIVADA, no la declarada. */
export async function createListing(body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return adaptListingAck(await call('/listings', { method: 'POST', body, signal }));
}

/** La FICHA. No mueve el estado: para eso está `setListingStatus`. */
export async function updateListing(id, body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return adaptListingAck(
    await call(`/listings/${encodeURIComponent(id)}`, { method: 'PATCH', body, signal }),
  );
}

/**
 * VENDER / PAUSAR / RETIRAR. Ruta propia, y esa separación es la que protege:
 * meterlo en el formulario es como guardar un precio republica un anuncio
 * retirado.
 */
export async function setListingStatus(id, status, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const api = STATUS_API[String(status).toLowerCase()];
  if (!api) throw new ListingInputError(`status: "${status}" no es un estado de anuncio.`, 'bad_status');
  const r = await call(`/listings/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: { status: api },
    signal,
  });
  return { id: r?.id ?? id, status: minusculas(r?.status) || String(status).toLowerCase(), soldAt: r?.soldAt ?? null };
}

/**
 * RETIRAR EL ANUNCIO.
 *
 * 🔴 NO ES UN BORRADO. En Firestore la pantalla hace `deleteDoc` y el anuncio
 * desaparece; el API no tiene DELETE y en su lugar lo pasa a `REMOVED`, que es
 * lo mismo que la propia `MyListings` ya hace al filtrar `status !== 'removed'`,
 * pero conservando la prueba. Un anuncio fraudulento borrado no se puede
 * revisar.
 */
export async function removeListing(id, opts = {}) {
  return setListingStatus(id, 'removed', opts);
}

/** La MODERACIÓN. Solo el admin, y restringir exige un motivo escrito. */
export async function setListingRestriction(id, restricted, reason, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const body = { restricted: restricted === true };
  if (body.restricted) {
    const motivo = nulable(reason);
    if (!motivo) {
      throw new ListingInputError(
        'reason: restringir un anuncio exige un motivo escrito.',
        'no_reason',
      );
    }
    body.reason = motivo;
  }
  const r = await call(`/listings/${encodeURIComponent(id)}/restriction`, {
    method: 'PATCH',
    body,
    signal,
  });
  return {
    id: r?.id ?? id,
    adminRestricted: r?.adminRestricted === true,
    adminRestrictionReason: r?.adminRestrictionReason ?? null,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// EL SELLO
// ────────────────────────────────────────────────────────────────────────────

/**
 * ABRIR EL EXPEDIENTE, Y SOLO DESDE EL ANUNCIO.
 *
 * 🔴 El identificador del anuncio va en la RUTA y el resto lo pone el servidor.
 * En Firestore la pantalla mandaba `listingId`, `sellerId`, `inspectorId`,
 * `carLabel` y hasta `status` dentro del cuerpo: cualquier taller podía crear
 * una inspección con un `listingId` ARBITRARIO y el disparador estampaba el
 * sello sin comprobar que ese anuncio hubiera pedido nada.
 */
export async function requestInspection(listingId, workshopId, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/listings/${encodeURIComponent(listingId)}/inspections`, {
    method: 'POST',
    body: { workshopId },
    signal,
  });
}

/**
 * COMPLETAR Y ESTAMPAR.
 *
 * 🔴 `overallScore` NO SE MANDA: lo CALCULA el servidor de los 12 puntos.
 * Recibirlo es el sello forjado, que es justo lo que esta ruta cierra. Y los
 * estados de los puntos van en mayúsculas.
 */
export async function completeInspection(inspectionId, { checkpoints, mechanicId, mechanicName }, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const nombre = nulable(mechanicName);
  if (!nombre) throw new ListingInputError('mechanicName: hay que firmar la inspección.', 'no_mechanic');
  const body = {
    status: 'COMPLETED',
    mechanicId: mechanicId || null,
    mechanicName: nombre,
    checkpoints: (checkpoints || []).map((c) => {
      const fila = { key: c.key, status: CHECKPOINT_STATUS_API[String(c.status || 'na').toLowerCase()] || 'NA' };
      const notas = nulable(c.notes);
      if (notas) fila.notes = notas;
      return fila;
    }),
  };
  return call(`/inspections/${encodeURIComponent(inspectionId)}`, { method: 'PATCH', body, signal });
}

/** El expediente. Público una vez estampado; si sigue abierto, solo los implicados. */
export async function fetchInspection(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    return await call(`/inspections/${encodeURIComponent(id)}`, { signal });
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;
  }
}

/** La cola del taller. El alcance ES el negocio, por eso cuelga de su ruta. */
export async function fetchWorkshopInspections(workshopId, { page = 1, pageSize = LISTINGS_PAGE_SIZE } = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/workshops/${encodeURIComponent(workshopId)}/inspections`, {
    query: { page, pageSize },
    signal,
  });
}

// ────────────────────────────────────────────────────────────────────────────
// EL BUZÓN
// ────────────────────────────────────────────────────────────────────────────

/**
 * Una sugerencia. El nombre y el correo salen del PERFIL, no del cuerpo: firmar
 * con el nombre de otro en una bandeja que lee el admin es barato y confuso.
 */
export async function createSuggestion(text, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const texto = nulable(text);
  if (!texto) throw new ListingInputError('text: no hay nada que enviar.', 'empty');
  return call('/suggestions', { method: 'POST', body: { text: texto }, signal });
}

/** La bandeja del admin. */
export async function fetchSuggestions({ page = 1, pageSize = LISTINGS_PAGE_SIZE } = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/suggestions', { query: { page, pageSize }, signal });
}

// ────────────────────────────────────────────────────────────────────────────
// LOS FALLOS
// ────────────────────────────────────────────────────────────────────────────

/** Cuánto del motivo cabe en un aviso sin volverse ilegible. */
const MAX_MOTIVO = 240;

/**
 * EL MOTIVO REAL DEL RECHAZO, para enseñárselo al vendedor.
 *
 * 🔴 El API contesta 400 NOMBRANDO el campo y 403 explicando la puerta que se
 * cerró ("para publicar hay que tener la identidad verificada"). Tragarse eso y
 * decir "Algo salió mal" deja al vendedor adivinando delante de un formulario de
 * seis pasos; decir "Publicado" cuando el servidor dijo que no es peor, y tiene
 * antecedentes en este proyecto.
 *
 * Solo se le enseña lo que viene del API (`ApiError`, incluidos los fallos de
 * red) o lo que rechazamos nosotros a conciencia (`ListingInputError`). Un
 * `TypeError` nuestro NO: eso es un fallo de programación, y disfrazarlo de
 * respuesta del servidor manda al vendedor a revisar un formulario que estaba
 * bien.
 */
export function listingErrorReason(err) {
  if (!err || typeof err.message !== 'string') return null;
  if (err.name !== 'ApiError' && err.name !== 'ListingInputError') return null;
  const msg = err.message.trim();
  if (!msg) return null;
  return msg.length > MAX_MOTIVO ? `${msg.slice(0, MAX_MOTIVO - 1)}…` : msg;
}

// ────────────────────────────────────────────────────────────────────────────
// EL STRIKE DE UN CASO DE SOPORTE
//
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║ 🔴 UNA LLAMADA QUE SUSTITUYE A CUATRO ESCRITURAS SUELTAS DEL NAVEGADOR.   ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
//
// Hoy `HelpCase.js` hace cuatro pasos por su cuenta: lee el array `strikes` del
// sancionado, lo reescribe entero, marca el caso como resuelto y escribe el
// aviso en el hilo. Eso tiene dos fallos que no son de estilo:
//
//   · La reescritura del array es leer-modificar-escribir SIN transacción: dos
//     admins a la vez PIERDEN UN STRIKE.
//   · Los cuatro pasos pueden quedarse a medias, y entonces hay un caso resuelto
//     sin sanción, o una sanción sin constancia en el hilo.
//
// El API lo hace en UNA transacción, con el strike como fila propia
// (`user_strike`, insertar no pisa nada) y con tres candados que aquí no había:
// solo admin, el sancionado tiene que ser PARTE del caso, y el motivo es
// obligatorio.
//
// ⚠️ ESTA LLAMADA TOCA DOS DOMINIOS A LA VEZ: el strike es de `users` y el caso
// (más su hilo) es de `listings`. Por eso quien la use tiene que exigir LOS DOS
// interruptores; con uno solo, el admin pulsa y el caso se resuelve en una base
// mientras la pantalla lo lee de la otra: el caso sigue abierto en pantalla y no
// hay ningún error que lo explique.
// ────────────────────────────────────────────────────────────────────────────

/** Anota un strike a una de las partes del caso. Solo admin; el API lo verifica. */
export async function issueTicketStrike(ticketId, { userId, reason }, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const quien = nulable(userId);
  const motivo = nulable(reason);
  // Se rechaza aquí lo que el API rechazaría igual, para no gastar un viaje —y
  // sobre todo para que el aviso diga QUÉ falta en vez de "El API respondió 400".
  if (!quien) throw new ListingInputError('userId: falta a quién se sanciona.', 'userId');
  if (!motivo) throw new ListingInputError('reason: un strike sin motivo no se puede apelar.', 'reason');
  return call(`/support/tickets/${encodeURIComponent(ticketId)}/strike`, {
    method: 'POST',
    body: { userId: quien, reason: motivo },
    signal,
  });
}

// ────────────────────────────────────────────────────────────────────────────
// EL EXPEDIENTE DEL SELLO, EN LOS NOMBRES QUE LA PANTALLA YA LEE
//
// 🔴 LOS ESTADOS VUELVEN EN MAYÚSCULAS y la app los compara en minúsculas, en
// los DOS niveles: el del expediente (`status === 'completed'` decide si
// `InspectionForm` se pinta en solo lectura) y el de cada uno de los 12 puntos
// (`STATUS_ICON[c.status]` de `InspectionReport`). Sin bajarlos, una inspección
// ya cerrada volvería a salir editable y los 12 puntos saldrían todos sin icono
// y sin color, y NADA fallaría, que es lo que hace caro este descuido.
// ────────────────────────────────────────────────────────────────────────────

/** Un expediente de inspección con los estados en minúscula. */
export function adaptInspection(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  return {
    ...row,
    id: row.id,
    status: minusculas(row.status) || 'requested',
    checkpoints: Array.isArray(row.checkpoints)
      ? row.checkpoints.map((c) => ({ ...c, status: minusculas(c && c.status) || 'na' }))
      : [],
  };
}

/** El expediente por su id, ya traducido. `null` cuando no existe o no es tuyo. */
export async function fetchInspectionRecord(id, opts = {}) {
  return adaptInspection(await fetchInspection(id, opts));
}

/**
 * EL SELLO DE UN CARRO REGISTRADO, para la insignia de su ficha.
 *
 * Solo devuelve las CERRADAS y solo las del que pregunta. Devuelve la más
 * reciente o `null`: la pantalla pinta una insignia, no una lista.
 */
export async function fetchVehicleInspection(vehicleId, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const payload = await call(`/vehicles/${encodeURIComponent(vehicleId)}/inspections`, { signal });
  const filas = payload && payload.items;
  if (!Array.isArray(filas)) {
    throw new ApiError('El API no devolvió una lista de inspecciones.', { code: 'bad_response' });
  }
  return filas.length ? adaptInspection(filas[0]) : null;
}

/** La cola del taller, ya traducida. La lista viene en `items`. */
export async function fetchWorkshopInspectionList(workshopId, params = {}, opts = {}) {
  const payload = await fetchWorkshopInspections(workshopId, params, opts);
  const filas = payload && payload.items;
  if (!Array.isArray(filas)) {
    throw new ApiError('El API no devolvió una lista de inspecciones.', { code: 'bad_response' });
  }
  return filas.map(adaptInspection).filter(Boolean);
}

// ────────────────────────────────────────────────────────────────────────────
// EL BUZÓN DE SUGERENCIAS — marcar una
// ────────────────────────────────────────────────────────────────────────────

/** Los tres estados que pinta la bandeja. Fuera de esta lista es un 400. */
export const SUGGESTION_STATUSES = ['new', 'reviewed', 'contacted'];

/**
 * MARCAR UNA SUGERENCIA. Solo admin, y el API lo comprueba.
 *
 * ⚠️ NO se manda `reviewedAt`: esa columna no existe y el esquema es `.strict()`,
 * así que mandarla tumbaría la llamada entera. Quién marcó y cuándo queda en el
 * registro de auditoría, que el API escribe EN LA MISMA transacción; el
 * `logAdminAction` del navegador se ejecuta aparte y por eso se puede perder.
 */
export async function setSuggestionStatus(id, status, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const s = String(status || '').toLowerCase();
  if (!SUGGESTION_STATUSES.includes(s)) {
    throw new ListingInputError(`status: "${status}" no es un estado de la bandeja.`, 'bad_status');
  }
  return call(`/suggestions/${encodeURIComponent(id)}`, { method: 'PATCH', body: { status: s }, signal });
}

// ────────────────────────────────────────────────────────────────────────────
// SOPORTE — los casos y su hilo
//
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║ 🔴 AQUÍ ESTÁ LA SEPARACIÓN POR ACTOR QUE `supportTickets` NUNCA TUVO.     ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
//
// En Firestore el AUTOR de un caso escribe CUALQUIER campo de su documento:
// `status`, `adminIntervened`, `strikeIssuedTo` y `strikeReason`. O sea, un
// cliente podía cerrar su propio caso como resuelto CON UN STRIKE ANOTADO CONTRA
// EL TALLER, y el panel del admin lo pintaba como si lo hubiera puesto un
// administrador. Por el API no hay un solo cuerpo donde quepan esos campos: el
// estado se mueve por `PATCH` (y la CONTRAPARTE no puede), el strike sale por su
// propia ruta y es solo del admin, y quién firma un mensaje lo decide el
// servidor a partir del token.
//
// ⚠️ LO QUE SE PIERDE: el TIEMPO REAL. Las pantallas escuchan hoy con
// `onSnapshot` y la respuesta del admin aparece sola. Por el API el hilo se
// refresca al recargar o tras escribir. Se acepta porque un caso de soporte se
// mide en horas, no en segundos.
// ────────────────────────────────────────────────────────────────────────────

/** Un caso, con los nombres que `Help`, `HelpCase` y el panel ya leen. */
export function adaptSupportTicket(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  return {
    ...row,
    id: row.id,
    type: minusculas(row.type) || 'general',
    status: minusculas(row.status) || 'open',
    // 🔴 En Postgres son DOS columnas porque en Firestore este campo mezcla dos
    // espacios de identificadores: en un caso de cita guarda un uid y en uno de
    // pedido el id del DOCUMENTO del taller. Como las pantallas comparan contra
    // `auth.uid`, el taller NUNCA veía un reclamo sobre un pedido suyo. La app
    // sigue leyendo un solo nombre, y aquí se le da el que SÍ es un uid.
    referenceOwnerId: row.referenceOwnerUserId ?? null,
    referenceOwnerName: row.referenceOwnerName ?? null,
    adminIntervened: row.adminIntervened === true,
  };
}

/**
 * Un mensaje del hilo.
 *
 * 🔴 La subcolección de Firestore tiene HOY DOS FORMAS incompatibles: el admin
 * escribe `{senderId}` y el cliente `{userId, userName, userRole, isStaff}`. Por
 * eso en el panel el hilo entero sale atribuido al mismo lado y en el caso del
 * cliente la respuesta del admin llega sin nombre. El API tiene UNA sola forma;
 * aquí se traduce a la que las pantallas ya pintan.
 */
export function adaptSupportMessage(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  return {
    id: row.id,
    userId: row.senderId ?? null,
    userName: row.senderName ?? '',
    userRole: minusculas(row.senderRole) || 'client',
    isStaff: row.isStaff === true,
    content: row.content ?? '',
    createdAt: row.createdAt ?? null,
  };
}

/** Abrir un caso. El nombre, el correo y la contraparte los pone el servidor. */
export async function createSupportTicket(
  { type, subject, description, referenceId, referenceLabel } = {},
  { apiFetchImpl, signal } = {},
) {
  const call = apiFetchImpl || apiFetch;
  const asunto = nulable(subject);
  const cuerpo = nulable(description);
  if (!asunto) throw new ListingInputError('subject: falta el asunto.', 'subject');
  if (!cuerpo) throw new ListingInputError('description: falta contar qué pasó.', 'description');
  // Lista de PERMITIDOS, no `...datos`: el esquema del API es `.strict()` y un
  // campo de más devuelve 400 nombrándolo. `referenceOwnerId` y
  // `referenceOwnerName`, que el formulario sigue calculando para el camino
  // viejo, NO viajan: el servidor los deriva de la referencia.
  const body = {
    type: String(type || 'general').toUpperCase(),
    subject: asunto,
    description: cuerpo,
  };
  const ref = nulable(referenceId);
  if (ref) body.referenceId = ref;
  const etiqueta = nulable(referenceLabel);
  if (etiqueta) body.referenceLabel = etiqueta;
  return adaptSupportTicket(await call('/support/tickets', { method: 'POST', body, signal }));
}

/**
 * La lista de casos.
 *
 * `scope` NO tiene valor por defecto "todos" a propósito: sin pedirlo son los
 * MÍOS. Un alcance que se ensancha al olvidarlo es como se sirve la bandeja de
 * otro.
 */
export async function fetchSupportTickets(
  { scope = 'mine', status = 'all', page = 1, pageSize = LISTINGS_PAGE_SIZE } = {},
  { apiFetchImpl, signal } = {},
) {
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/support/tickets', { query: { scope, status, page, pageSize }, signal });
  const filas = payload && payload.items;
  if (!Array.isArray(filas)) {
    throw new ApiError('El API no devolvió una lista de casos.', { code: 'bad_response' });
  }
  return {
    tickets: filas.map(adaptSupportTicket).filter(Boolean),
    page: Number(payload.page) || page,
    pageSize: Number(payload.pageSize) || pageSize,
    total: Number.isFinite(Number(payload.total)) ? Number(payload.total) : null,
  };
}

/**
 * La ficha con su hilo.
 *
 * ⚠️ EL 404 NO ES UN ERROR, ES UNA RESPUESTA: un caso lleva dentro el nombre y
 * el correo de quien lo abrió, así que a quien no es parte se le contesta lo
 * mismo que si no existiera. Cualquier otro fallo SÍ sube.
 */
export async function fetchSupportTicket(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  let payload;
  try {
    payload = await call(`/support/tickets/${encodeURIComponent(id)}`, { signal });
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;
  }
  const ticket = adaptSupportTicket(payload);
  if (!ticket) return null;
  return {
    ticket,
    // El papel de quien mira lo dice el servidor: la pantalla lo necesita para
    // saber qué ofrecer, y decidirlo en el navegador es decidirlo donde se puede
    // cambiar.
    viewerRole: minusculas(payload.viewerRole) || null,
    messages: (Array.isArray(payload.messages) ? payload.messages : [])
      .map(adaptSupportMessage)
      .filter(Boolean),
  };
}

/**
 * RESPONDER. El cuerpo es SOLO el texto: quién firma, con qué rol y si es
 * personal de MekaFix lo decide el servidor a partir del token. Hoy eso viaja en
 * el cuerpo, o sea que cualquiera puede firmar como staff.
 *
 * El servidor mueve además el estado del caso en la MISMA transacción (y
 * escribir en uno cerrado lo reabre), así que aquí no hay una segunda llamada
 * que pueda quedarse a medias.
 */
export async function postTicketMessage(id, content, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const texto = nulable(content);
  if (!texto) throw new ListingInputError('content: no hay nada que enviar.', 'empty');
  return adaptSupportMessage(
    await call(`/support/tickets/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: { content: texto },
      signal,
    }),
  );
}

/**
 * CERRAR O DAR POR RESUELTO. Del AUTOR o del ADMIN.
 *
 * 🔴 La CONTRAPARTE no puede, y el API lo rechaza: dar por resuelto el reclamo
 * que va contra uno mismo no es resolverlo. Reabrir no está aquí — se reabre
 * escribiendo en el hilo, que es el único camino que además avisa.
 */
export async function setTicketStatus(id, status, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const s = String(status || '').toUpperCase();
  if (s !== 'RESOLVED' && s !== 'CLOSED') {
    throw new ListingInputError(`status: "${status}" no cierra un caso.`, 'bad_status');
  }
  return adaptSupportTicket(
    await call(`/support/tickets/${encodeURIComponent(id)}`, { method: 'PATCH', body: { status: s }, signal }),
  );
}
